import React, { useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useTilesContext, TilesProvider } from "../../components/Unique/Tiles/tiles_context";
import TilesetPreview from "../../components/Unique/Tiles/tileset_preview";
import StructurePreview from "../../components/Unique/Tiles/structure_preview";
import { toast } from "sonner";
import JSZip from "jszip";
import { useStatusContext } from "../../components/Page/status_context";

// InfoOverlay component for help popup
const InfoOverlay = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
      <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <svg width="22" height="22" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="256" cy="256" r="216" stroke="black" strokeWidth="32" fill="none"/>
            <rect x="232" y="200" width="48" height="160" rx="24" fill="black"/>
            <circle cx="256" cy="144" r="24" fill="black"/>
          </svg>
          How does the tileset uploader work?
        </h2>
        <div className="space-y-3 text-gray-800 text-sm dark:text-white">
          <p>
            The uploader accepts a <b>.zip file</b> containing your tileset images, organized in folders. Each folder should represent a layer or group, and must contain PNG images. The structure should include an <b>Icon.png</b> at the root or in a base folder.
          </p>
          <div className="bg-gray-100 rounded p-3 mt-2 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white font-mono">
            tileset-3/<br/>
            ├── 0/<br/>
            │   ├── Tile_0.png<br/>
            │   └── Tile_1.png<br/>
            ├── 1/<br/>
            │   ├── Tile_0.png<br/>
            │   ├── Tile_1.png<br/>
            │   ├── Tile_2.png<br/>
            │   ├── Tile_3.png<br/>
            │   ├── Tile_4.png<br/>
            ├── 2/<br/>
            │   ├── Tile_0.png<br/>
            │   ├── Tile_1.png<br/>
            │   ├── Tile_2.png<br/>
            │   ├── Tile_3.png<br/>
            │   ├── Tile_4.png<br/>
            │   ├── Tile_5.png<br/>
            ├── 3/<br/>
            │   ├── Tile_0.png<br/>
            │   ├── Tile_1.png<br/>
            │   ├── Tile_2.png<br/>
            │   ├── Tile_3.png<br/>
            └── Icon.png
          </div>
          <ul className="list-disc pl-5 space-y-1">
            <li>Each folder (e.g., <code>0</code>, <code>1</code>, ...) should contain images named <code>Tile_0.png</code>, <code>Tile_1.png</code>, etc.</li>
            <li>The uploader will preview your tileset and its structure after upload.</li>
            <li>Make sure your zip file is not corrupted and follows the expected structure for best results.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const CombinationsPlanningInner = () => {
  const { setArchive, archive, images, setImages } = useTilesContext();
  const [error, setError] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const statusContext = useStatusContext();
  const setStatus = statusContext?.setStatus || (() => {});
  const setLoading = statusContext?.setLoading || (() => {});

  // Helper to encode files in the zip for S3 upload
  const encodeFiles = async (zipFile) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = async (event) => {
        try {
          const zip = await JSZip.loadAsync(event.target.result);
          const filePromises = Object.keys(zip.files).map((filename) =>
            zip.files[filename].async("base64").then((content) => ({ filename, content }))
          );
          const encodedFiles = await Promise.all(filePromises);
          resolve(encodedFiles);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(zipFile);
    });
  };

  const handleUpload = async () => {
    if (!archive || !confirmed) return;
    setUploading(true);
    setStatus({ text: "Uploading...", color: 1 });
    setLoading(true);
    try {
      const encodedFiles = await encodeFiles(archive);
      for (const file of encodedFiles) {
        await fetch(process.env.REACT_APP_API_URL_S3, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ method: "uploadFile", filename: file.filename, content: file.content }),
          credentials: "include",
        });
      }
      setStatus({ text: "Uploading successful...", color: 0 });
      toast.success("Successfully uploaded tileset to S3!");
      setConfirmed(false);
    } catch (err) {
      setStatus({ text: "Uploading failed...", color: 1 });
      toast.error("Failed to upload tileset to S3.");
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".zip")) {
      setError("Please upload a .zip file containing your tileset images.");
      return;
    }
    setError("");
    setArchive(file);
  };

  const handleClear = () => {
    setArchive([]);
    setImages({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setError("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white m-0 p-0">Tileset Uploader</h3>
        {archive && archive.name && (
          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{archive.name}</span>
        )}
      </div>
      <div className="space-y-3">
        <Input
          type="file"
          accept=".zip"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div className="flex gap-3">
          <Button
            className="bg-purple-700 hover:bg-purple-800 text-white font-medium px-6 py-2 shadow-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Tileset (.zip)
          </Button>
          <Button
            onClick={handleClear}
            variant="outline"
            className="border-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900 font-medium bg-transparent"
          >
            Clear
          </Button>
        </div>
        {/* Info message for upload */}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
          <span>
            📁 Upload a .zip archive containing the tileset images
          </span>
          {/* Info Button */}
          <button
            className="ml-2 p-1 rounded-full flex items-center justify-center"
            onClick={() => setShowInfo(true)}
            aria-label="How does the uploader work?"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="256" cy="256" r="216" stroke="currentColor" strokeWidth="40" fill="none"/>
              <rect x="232" y="200" width="48" height="160" rx="24" fill="currentColor"/>
              <circle cx="256" cy="144" r="24" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
        {error && <div className="text-red-600 dark:text-red-400 font-medium mt-2">{error}</div>}
      </div>
      {Object.keys(images).length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
            <div>
              <TilesetPreview />
            </div>
            <div>
              <StructurePreview />
            </div>
          </div>
          {/* Confirmation and Upload Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mt-6">
            <Button
              className={`font-medium py-3 ${confirmed ? "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white" : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white"}`}
              onClick={() => setConfirmed((prev) => !prev)}
              disabled={uploading}
            >
              {confirmed ? "✓ Confirmed" : "Confirm Upload"}
            </Button>
            <Button
              className={`font-medium py-3 ${confirmed ? "bg-purple-700 hover:bg-purple-800 dark:bg-purple-800 dark:hover:bg-purple-900 text-white" : "bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"}`}
              onClick={handleUpload}
              disabled={!confirmed || uploading}
            >
              {uploading ? "Uploading..." : "Upload to S3"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const CombinationsPlanning = () => (
  <TilesProvider>
    <CombinationsPlanningInner />
  </TilesProvider>
);

export default CombinationsPlanning; 